import { useMemo } from 'react';

import useGlobalError from '@/core/hooks/api/useGlobalErrors';
import FavoritesDomain from '@/core/lib/new-architecture/domain/favorites.domain';
import Query from '@/core/lib/new-architecture/query';
import Store from '@/core/lib/new-architecture/store';
import type { InfiniteStoreData } from '@/core/lib/new-architecture/store/store.types';

const useFavorites = (): InfiniteStoreData<FavoritesDomain> => {
  const { requestToken } = Store.auth.useRequest();
  const { data: categories, isLoading: categoriesIsLoading } = Store.categories.useCategories();
  const { data: current, isLoading: currentIsLoading } = Store.current.useCurrent();

  const { data: savedSearches, isLoading: savedSearchesIsLoading, error } = Query.savedSearch.useSavedSearches({ token: requestToken, enabled: Boolean(current) });
  const { data: followedDonations, isLoading: followedDonationsIsLoading } = Query.followedDonations.useFollowedDonations({ token: requestToken, enabled: Boolean(current) });
  const {
    data: contactsDonationsPages,
    isLoading: contactsDonationsIsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = Query.contactsDonations.useContactsDonations({ token: requestToken, enabled: Boolean(current) });

  const isLoading = savedSearchesIsLoading || categoriesIsLoading || followedDonationsIsLoading || currentIsLoading || contactsDonationsIsLoading;

  useGlobalError(error);

  const data = useMemo(() => {
    if (!savedSearches || !categories || !followedDonations || !current || !contactsDonationsPages) {
      return null;
    }

    return new FavoritesDomain({ savedSearches, categories, followedDonations, current, contactsDonationsPages });
  }, [savedSearches, categories, followedDonations, current, contactsDonationsPages]);

  return {
    data,
    isLoading,
    notFound: false,
    next: fetchNextPage,
    hasNext: hasNextPage,
    isFetchingNextPage,
    total: contactsDonationsPages?.pages.reduce((acc, page) => acc + page.donations.length, 0) ?? 0,
  };
};

export default { useFavorites };
